<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useBankId } from '@/composables/useBankId'
import AgreementCard from '../AgreementCard.vue'
import type { Agreement, SigningStatus } from '../types'
import type { FStepperStepProps, FStepperStepExpose } from '@/components/feature/FStepper/types'
import mdiCheck from '~icons/mdi/check'
import mdiAlertOutline from '~icons/mdi/alert-outline'

interface AgreementSignStepProps extends FStepperStepProps {
  stepProps: {
    agreements: Agreement[]
  }
}

const props = defineProps<AgreementSignStepProps>()

defineModel<Record<string, unknown>>({ required: true })

const { t } = useI18n()

const signingStatus = ref<SigningStatus>('filling_form')
const documentUserVisibleData = ref<Record<string, string>>({})
const stepCompleted = computed(() => signingStatus.value === 'completed')

defineExpose<FStepperStepExpose>({ stepCompleted })

const agreements = computed(() => props.stepProps.agreements)

const userVisibleDataMessage = computed(() => {
  let message = `${t('common.sign_documents')}\n\n`
  for (const data of Object.values(documentUserVisibleData.value)) {
    message += `${data}\n`
  }
  return message
})

const bankId = useBankId('signing', () => ({
  signing: {
    data: {
      resource: 'agreements',
      documents_to_sign: agreements.value,
    },
    message: userVisibleDataMessage.value,
  },
  endpointOverrides: {
    createSigningToken: 'mobile-bankid--signs/create-signing-token',
    init: 'mobile-bankid--signs',
    poll: 'mobile-bankid--signs/{signing_token}',
    cancel: 'mobile-bankid--signs/{signing_token}',
  },
}))

const hintCodeMessage = computed(() => {
  const hintCode = bankId.hintCode.value
  if (hintCode === 'userCancel') return t('common.signing_was_canceled')
  if (hintCode === 'startFailed' || hintCode === 'expiredTransaction') return t('common.signing_took_to_long')
  return null
})

const errorMessage = computed(() => {
  if (hintCodeMessage.value != null) return hintCodeMessage.value

  const error = bankId.error.value
  if (error == null) return ''
  if (error._locale_message != null) return t(error._locale_message)
  if (typeof error._error === 'string') return error._error
  if (
    error._error != null &&
    typeof error._error === 'object' &&
    'message' in error._error &&
    error._error.message != null
  ) {
    return error._error.message
  }
  return t('system.error')
})

const qrDataUrl = computed(() =>
  bankId.qrBase64.value != null ? `data:image/svg+xml;base64,${bankId.qrBase64.value}` : ''
)

watch(
  () => bankId.status.value,
  (status) => {
    if (status === 'completed') {
      signingStatus.value = 'completed'
    } else if (status === 'failed') {
      signingStatus.value = 'error'
    } else if (status === 'pending') {
      signingStatus.value = 'pending'
    }
  }
)

watch(
  () => bankId.error.value,
  (error) => {
    if (error != null) {
      signingStatus.value = 'error'
    }
  }
)

function updateUserVisibleData(agreementId: string, data: string) {
  documentUserVisibleData.value[agreementId] = data
}

async function startSigning() {
  signingStatus.value = 'pending'
  try {
    await bankId.start()
  } catch {
    signingStatus.value = 'error'
  }
}

async function cancelSigning() {
  try {
    await bankId.cancelLogin()
  } finally {
    bankId.reset()
    signingStatus.value = 'filling_form'
  }
}

function tryAgain() {
  bankId.reset()
  signingStatus.value = 'filling_form'
}

onUnmounted(() => {
  bankId.reset()
})
</script>

<template>
  <section>
    <article v-if="signingStatus === 'filling_form'">
      <v-row
        class="mt-0"
        justify="space-between"
        no-gutters
      >
        <agreement-card
          v-for="agreement in agreements"
          :key="agreement.id"
          :agreement="agreement"
          @update:user-visible-data="updateUserVisibleData(agreement.id, $event)"
        />
      </v-row>

      <v-row
        class="mt-0 mb-4"
        justify="center"
        no-gutters
      >
        <v-btn
          color="primary"
          @click="startSigning"
        >
          {{ t(agreements.length > 1 ? 'common.sign_agreements' : 'common.sign_agreement') }}
        </v-btn>
      </v-row>
    </article>

    <article v-else-if="signingStatus === 'pending'">
      <v-row
        class="mb-2 pt-1"
        justify="center"
        no-gutters
      >
        <v-card
          class="mt-4 d-flex justify-center align-center"
          height="200"
          width="200"
          variant="outlined"
        >
          <v-img
            v-if="qrDataUrl"
            :src="qrDataUrl"
            width="100%"
            aspect-ratio="1"
            :alt="t('common.bankid_qr')"
          />
        </v-card>
      </v-row>
      <v-row
        class="text-center"
        justify="center"
        no-gutters
      >
        <v-col cols="6">
          {{ t('common.bankid_qr_sign_information') }}
        </v-col>
      </v-row>
      <v-row
        class="mb-1"
        justify="center"
        no-gutters
      >
        <v-btn
          class="mt-4"
          color="primary"
          variant="outlined"
          size="small"
          @click="cancelSigning"
        >
          {{ t('common.cancel') }}
        </v-btn>
      </v-row>
    </article>

    <article
      v-else-if="signingStatus === 'completed'"
      class="pa-6"
    >
      <v-row
        align="center"
        class="text-center"
        justify="center"
        no-gutters
      >
        <v-icon
          :icon="mdiCheck"
          color="primary"
          size="x-large"
          :aria-label="t('common.sign_agreement_done')"
        />
      </v-row>
      <v-row
        class="text-center mb-4"
        justify="center"
        no-gutters
      >
        <span class="text-headline-small">{{ t('common.sign_agreement_done') }}</span>
      </v-row>
    </article>

    <article
      v-else-if="signingStatus === 'error'"
      class="pa-6"
    >
      <v-row
        align="center"
        class="text-center"
        justify="center"
        no-gutters
      >
        <v-icon
          :icon="mdiAlertOutline"
          color="error"
          size="large"
          :aria-label="t('mobile_bankid.error')"
        />
        <v-card
          color="transparent"
          variant="flat"
        >
          <v-card-title>
            {{ t('mobile_bankid.error') }}
          </v-card-title>
          <v-card-subtitle v-if="errorMessage">
            {{ errorMessage }}
          </v-card-subtitle>
        </v-card>
      </v-row>
      <v-row
        class="mt-4 mb-8"
        justify="center"
        no-gutters
      >
        <v-btn
          color="primary"
          @click="tryAgain"
        >
          {{ t('common.try_again') }}
        </v-btn>
      </v-row>
    </article>
  </section>
</template>
